/* External */
import { forwardRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import ShakaPlayerCommon from '@dtvgo/shaka-player-react';
import { removePeriodsWithoutAudio } from '@dtvgo/player-utils';

/* Utils */
import {
  bufferConfigShape,
  keySystemsConfigShape,
  restrictionsShape,
  sourceShape,
} from '../utils/propTypes';
import { noOp } from '../../../utils/functions';
import { useConfig } from '../../../providers/ConfigProvider';
import { LL_CATCH_UP_ACTIVE } from '../../../utils/env';

/* Styles */
import './ShakaPlayer.scss';

const ShakaPlayer = forwardRef(({
  className,
  readyToBuild,
  isLive,
  restrictions,
  abrRestrictions,
  bufferConfig,
  presentationDelay,
  lowLatency,
  keySystemsConfig,
  source,
  shouldPlay,
  startOffset,
  recovery,
  stallMechanism,
  shouldReloadOnError,
  npawAdapterOptions,
  onReadyToPlay,
  onPlay,
  onPlaying,
  onPause,
  onProgress,
  onEnded,
  onDurationChange,
  onBuffering,
  onBufferingEnded,
  onSeeked,
  onHDCPError,
  onUnrecoverableError,
  onFailedLicense,
  onNonPlayableSources,
  onSourceLoaded,
  onSourceUnloaded,
  onTracksEvent,
  onWarning,
  singleAudioTrackLanguage,
  onResponseReceived,
  getManifestHeaders,
  getSegmentHeaders,
  onRequest,
  children,
}, ref) => {
  const { player: { requestConfig } = {}, isFetching } = useConfig();

  const config = useMemo(
    () => (
      /** @type {shaka.extern.PlayerConfiguration} */
      {
        restrictions,
        streaming: {
          autoLowLatencyMode: true,
          retryParameters: requestConfig,
          rebufferingGoal: bufferConfig?.rebufferingGoal ?? undefined,
          ignoreTextStreamFailures: recovery?.ignoreTextStreamFailures ?? true,
          stallEnabled: stallMechanism?.enabled,
          stallThreshold: stallMechanism?.threshold,
        },
        manifest: {
          defaultPresentationDelay: presentationDelay,
          retryParameters: requestConfig,
          dash: {
            ignoreSuggestedPresentationDelay: !!presentationDelay,
            manifestPreprocessor: removePeriodsWithoutAudio,
          },
          // Workaround for https://github.com/shaka-project/shaka-player/issues/5482
          // It happens with Paramount+ content that only uses HLS manifest (See jira VP-955)
          // Other content use DASH
          // Remove when Shaka fix it
          // Rethink if we need to use HLS manifest for non Paramount+ content
          hls: {
            sequenceMode: false,
            ignoreManifestTimestampsInSegmentsMode: !isLive,
          },
        },
        drm: {
          retryParameters: requestConfig,
        },
        abr: {
          restrictions: abrRestrictions,
        },
      }
    ),
    [
      abrRestrictions,
      bufferConfig.rebufferingGoal,
      presentationDelay,
      recovery?.ignoreTextStreamFailures,
      stallMechanism,
      requestConfig,
      restrictions,
      isLive,
    ],
  );

  const convertedRecovery = useMemo(() => ({
    ...recovery,
    asWarning: {
      ...recovery?.asWarning,
      errorCodes: recovery?.asWarning?.errorCodes && new Set(recovery.asWarning?.errorCodes),
    },
    reload: {
      skipSeconds: 2,
      ...recovery?.reload,
      errorCodes: recovery?.reload?.errorCodes && new Set(recovery.reload?.errorCodes),
      shouldReload: shouldReloadOnError,
    },
  }), [recovery, shouldReloadOnError]);

  return (
    <ShakaPlayerCommon
      className={className}
      readyToBuild={readyToBuild && !isFetching}
      config={config}
      bufferConfig={bufferConfig}
      lowLatency={lowLatency}
      keySystemsConfig={keySystemsConfig}
      source={source}
      shouldPlay={shouldPlay}
      isLive={isLive}
      startOffset={startOffset}
      recovery={convertedRecovery}
      npawAdapterOptions={npawAdapterOptions}
      onReadyToPlay={onReadyToPlay}
      onPlay={onPlay}
      onPlaying={onPlaying}
      onPause={onPause}
      onProgress={onProgress}
      onEnded={onEnded}
      onDurationChange={onDurationChange}
      onBuffering={onBuffering}
      onBufferingEnded={onBufferingEnded}
      onSeeked={onSeeked}
      onHDCPError={onHDCPError}
      onUnrecoverableError={onUnrecoverableError}
      onFailedLicense={onFailedLicense}
      onNonPlayableSources={onNonPlayableSources}
      onSourceLoaded={onSourceLoaded}
      onSourceUnloaded={onSourceUnloaded}
      onWarning={onWarning}
      onTracksEvent={onTracksEvent}
      isCatchUpActive={LL_CATCH_UP_ACTIVE}
      singleAudioTrackLanguage={singleAudioTrackLanguage}
      onResponseReceived={onResponseReceived}
      getManifestHeaders={getManifestHeaders}
      getSegmentHeaders={getSegmentHeaders}
      onRequest={onRequest}
      ref={ref}
    >
      {children}
    </ShakaPlayerCommon>
  );
});

ShakaPlayer.propTypes = {
  className: PropTypes.string,
  readyToBuild: PropTypes.bool,
  isLive: PropTypes.bool,
  singleAudioTrackLanguage: PropTypes.string,
  restrictions: restrictionsShape,
  abrRestrictions: restrictionsShape,
  bufferConfig: PropTypes.shape({
    forward: PropTypes.number,
    backward: PropTypes.number,
    rebufferingGoal: PropTypes.number,
    audio: PropTypes.shape(bufferConfigShape),
    video: PropTypes.shape(bufferConfigShape),
  }),
  presentationDelay: PropTypes.number,
  lowLatency: PropTypes.shape({
    targetLatency: PropTypes.number,
    catchup: PropTypes.shape({
      playbackRate: PropTypes.number,
      playbackRateThreshold: PropTypes.number,
      seekThreshold: PropTypes.number,
    }),
    fallback: PropTypes.shape({
      playbackRate: PropTypes.number,
      playbackRateThreshold: PropTypes.number,
      seekThreshold: PropTypes.number,
    }),
  }),
  keySystemsConfig: keySystemsConfigShape,
  source: sourceShape,
  shouldPlay: PropTypes.bool,
  startOffset: PropTypes.number,
  recovery: PropTypes.shape({
    ignoreTextStreamFailures: PropTypes.bool,
    asWarning: PropTypes.shape({ errorCodes: PropTypes.arrayOf(PropTypes.number) }),
    reload: PropTypes.shape({
      errorCodes: PropTypes.arrayOf(PropTypes.number),
      skipSeconds: PropTypes.number,
      backoffOptions: PropTypes.shape({
        maxAttempts: PropTypes.number,
        baseDelay: PropTypes.number,
        backoffFactor: PropTypes.number,
        fuzzFactor: PropTypes.number,
        resetTime: PropTypes.number,
      }),
    }),
  }),
  stallMechanism: PropTypes.shape({
    enabled: PropTypes.bool,
    threshold: PropTypes.number,
  }),
  npawAdapterOptions: PropTypes.shape({
    errorParser: PropTypes.func,
    getErrorDetails: PropTypes.func,
  }),
  shouldReloadOnError: PropTypes.func,
  onReadyToPlay: PropTypes.func,
  onPlay: PropTypes.func,
  onPlaying: PropTypes.func,
  onPause: PropTypes.func,
  onProgress: PropTypes.func,
  onEnded: PropTypes.func,
  onDurationChange: PropTypes.func,
  onBuffering: PropTypes.func,
  onBufferingEnded: PropTypes.func,
  onSeeked: PropTypes.func,
  onHDCPError: PropTypes.func,
  onUnrecoverableError: PropTypes.func,
  onFailedLicense: PropTypes.func,
  onNonPlayableSources: PropTypes.func,
  onSourceLoaded: PropTypes.func,
  onSourceUnloaded: PropTypes.func,
  onTracksEvent: PropTypes.func,
  onWarning: PropTypes.func,
  onRequest: PropTypes.func,
  onResponseReceived: PropTypes.func,
  getSegmentHeaders: PropTypes.func,
  getManifestHeaders: PropTypes.func,
  children: PropTypes.node,
};

ShakaPlayer.defaultProps = {
  className: '',
  readyToBuild: true,
  isLive: false,
  singleAudioTrackLanguage: undefined,
  restrictions: undefined,
  abrRestrictions: undefined,
  bufferConfig: undefined,
  presentationDelay: undefined,
  lowLatency: undefined,
  keySystemsConfig: undefined,
  source: undefined,
  shouldPlay: false,
  startOffset: 0,
  recovery: undefined,
  stallMechanism: undefined,
  shouldReloadOnError: undefined,
  npawAdapterOptions: undefined,
  onReadyToPlay: noOp,
  onPlay: noOp,
  onPlaying: noOp,
  onPause: noOp,
  onProgress: noOp,
  onEnded: noOp,
  onDurationChange: noOp,
  onBuffering: noOp,
  onBufferingEnded: noOp,
  onSeeked: noOp,
  onHDCPError: noOp,
  onUnrecoverableError: noOp,
  onFailedLicense: noOp,
  onNonPlayableSources: noOp,
  onSourceLoaded: noOp,
  onSourceUnloaded: noOp,
  onTracksEvent: noOp,
  onWarning: noOp,
  onRequest: undefined,
  onResponseReceived: () => {},
  getSegmentHeaders: () => {},
  getManifestHeaders: () => {},
  children: undefined,
};

export default memo(ShakaPlayer);
